import { Pipe, PipeTransform } from '@angular/core';
import { LocalizedDateService } from '../services/localized-date.service';
import { IDateFormatOptions } from '../models/date-time-format-options.model';

/**
 * Pipe for formatting dates with timezone and locale support.
 * Uses the browser timezone by default. See IDateFormatOptions
 * for all format options.
 *
 * @example
 * ```html
 * <!-- Using predefined format -->
 * {{ date | localizedDate:'long':'America/New_York' }}
 *
 * <!-- Using custom format options -->
 * {{ date | localizedDate:{ weekday: 'long', year: 'numeric' } }}
 * ```
 */
@Pipe({
  name: 'localizedDate',
  standalone: true
})
export class LocalizedDatePipe implements PipeTransform {
  constructor(
    private localizedDateService: LocalizedDateService,
  ) {}

  /**
   * Transform a date to localized format
   * @param value - Date value
   * @param format - Format type or custom format options
   * @param timezone - Optional timezone override
   * @param locale - Optional locale override
   */
  transform(
    value: Date | string | null | undefined,
    format: IDateFormatOptions['format'] | Intl.DateTimeFormatOptions = 'medium',
    timezone?: string,
    locale?: string
  ): string {
    if (!value) {
      return '';
    }

    const options: IDateFormatOptions = {
      timezone,
      locale
    };

    // Check if format is a string (predefined format) or object (custom format)
    if (typeof format === 'string') {
      options.format = format;
    } else {
      options.customFormat = format;
    }

    return this.localizedDateService.formatDate(value, options);
  }
}
